// src/components/RouteGuards/ProtectedRoute.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { getCurrentLoggedInUser } from "../../services/authService";
import { useStateContext } from "../../context/ContextProvider";
import FullScreenLoader from "../Loaders/FullScreenLoader";
import { LOGIN_PATH } from "../../router/routes";

const matchRole = (userRole, required) => {
  if (!required) return true; // no role required
  return Array.isArray(required)
    ? required.includes(userRole)
    : userRole === required;
};

const ProtectedRoute = ({
  element,
  role, // string | string[]
  redirectToLogin = LOGIN_PATH,
  redirectToUnauthorized = "/unauthorized",
  showLoaderText = "Loading, Please wait...",
}) => {
  const { user, setUser, accessToken } = useStateContext();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const loc = useLocation();
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  useEffect(() => {
    const run = async () => {
      // 1) No token? Go straight to login. No API call.
      if (!accessToken) {
        setAuthorized(false);
        setLoading(false);
        return;
      }

      // 2) If we already have user, just check role.
      if (user) {
        setAuthorized(matchRole(user?.role?.name, role));
        setLoading(false);
        return;
      }

      // 3) Otherwise fetch user once.
      try {
        const { data } = await getCurrentLoggedInUser();
        const u = data?.data;
        setUser(u);
        if (isMounted.current) {
          setAuthorized(matchRole(u?.role?.name, role));
        }
      } catch {
        // Token invalid/expired → treat as unauthenticated; don't send to /unauthorized.
        if (isMounted.current) {
          setAuthorized(false);
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    run();
    // Depend only on token and whether we already have a user; avoid loops on setUser
  }, [accessToken, !!user, role, setUser]);

  if (!accessToken) {
    // No token → login (preserve intended route via state, optional)
    return <Navigate to={redirectToLogin} replace state={{ from: loc }} />;
  }

  if (loading) return <FullScreenLoader loadingText={showLoaderText} />;

  // Have token but failed to fetch user (expired/invalid) → login
  if (!user) {
    return <Navigate to={redirectToLogin} replace state={{ from: loc }} />;
  }

  // Authenticated but wrong role → unauthorized
  if (!authorized) {
    return <Navigate to={redirectToUnauthorized} replace />;
  }

  return element;
};

export default ProtectedRoute;
